import { Component, DestroyRef, OnInit, ViewEncapsulation, ChangeDetectionStrategy, Injector, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ModalController } from '@ionic/angular';
import { FilterForm } from '@shared/classes/calendar/filter-form.class';
import { FilterGroup } from '@shared/classes/calendar/filter-group.class';
import { CALENDAR_VIEWS } from '@shared/constants';
import { PAGINATION } from '@shared/constants/pagination.const';
import { IFilterFormParams } from '@shared/interfaces';
import { CalendarService } from '@core/services/system/calendar/calendar.service';

@Component({
  template: '',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export abstract class CalendarFilterModalBaseComponent implements OnInit {
  // TODO need refactoring to signal: legacy public fields are kept as a compatibility layer for subclasses/tests.
  public filterForm = new FilterForm();
  public filterFormParams: IFilterFormParams = {};
  public isDisabled = false;
  public showRemoveButton = false;
  public readonly filterFormState = signal<FilterForm>(this.filterForm);
  public readonly isDisabledState = signal(this.isDisabled);
  public readonly showRemoveButtonState = signal(this.showRemoveButton);
  protected modalController: ModalController;
  protected calendarService: CalendarService;
  protected filterFormInit = new FilterForm();
  protected abstract view: CALENDAR_VIEWS;
  private destroyRef: DestroyRef;

  constructor(protected injector: Injector) {
    this.modalController = this.injector.get(ModalController);
    this.calendarService = this.injector.get(CalendarService);
    this.destroyRef = this.injector.get(DestroyRef);
  }

  ngOnInit() {
    this.calendarService.getFilterForm(this.view).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((filterForm) => {
      this.setFilterFormState(filterForm);
    });
    const filterForm = this.calendarService.getFilterFormValue(this.view);
    this.filterFormInit = filterForm.copy();
    const defaultFilterForm = this.calendarService.getDefaultFilterForm(this.view);
    this.setRemoveButton(defaultFilterForm.selectedGroups);
  }

  async closeModal() {
    await this.dismiss();
    this.calendarService.setFilterForm(this.view, this.filterFormInit);
  }

  async confirmModal() {
    await this.dismiss();
    this.calendarService.setUpdateStatus({ status: PAGINATION.LIST_UPDATE_SOURCE.INIT });
  }

  async clearFilters() {
    await this.dismiss();
    const defaultFilterForm = this.calendarService.getDefaultFilterForm(this.view);
    this.calendarService.setFilterForm(this.view, defaultFilterForm.copy());
    this.calendarService.setUpdateStatus({ status: PAGINATION.LIST_UPDATE_SOURCE.INIT });
  }

  setDisabled() {
    const selectedGroups = this.filterForm.selectedGroups.filter((group) => !group.isHidden);
    this.setIsDisabledState(!!!selectedGroups.length);
  }

  private dismiss() {
    return this.modalController.dismiss();
  }

  private setFilterFormState(filterForm: FilterForm): void {
    this.filterForm = filterForm;
    this.filterFormState.set(filterForm);
  }

  private setIsDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this.isDisabledState.set(isDisabled);
  }

  private setShowRemoveButtonState(showRemoveButton: boolean): void {
    this.showRemoveButton = showRemoveButton;
    this.showRemoveButtonState.set(showRemoveButton);
  }

  private setRemoveButton(defaultGroup: FilterGroup[]) {
    let { selectedGroups } = this.filterForm;
    defaultGroup = defaultGroup.filter((filterGroup) => !filterGroup.isHidden);
    selectedGroups = selectedGroups.filter((filterGroup) => !filterGroup.isHidden);

    this.setShowRemoveButtonState(JSON.stringify(defaultGroup) !== JSON.stringify(selectedGroups));
  }
}
